<script setup lang="ts">
import BaseIcon from '@c/common/Icon.vue';
import BaseDialog from '@c/common/Dialog.vue';
import BaseButton from '@c/common/Button.vue';
import { toast } from '@/utils/toast';
import type { PropType } from 'vue';

// ----------------- 以下代码从 new-web-exchange 拷贝，不要在此处修改（缩进也不要改） -----------------
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { ucApi } from '@/config/api';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';

// 扩展参数（为了兼容 passport、新老架构各种情况，额外参数放在这里）
type ExtraParams = {
    requestId?: string // passport 场景专用
};

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
    biz: {
        type: String as PropType<PasskeyBizType>,
        required: true,
    },
    bizParams: {
        type: Object,
        required: true,
    },
    extraParams: {
        type: Object as PropType<ExtraParams>,
        default: () => ({}),
    },
    // 是否允许降级，如果不传入，则根据 biz 走接口查询
    downgradeAble: {
        type: Boolean as PropType<boolean | undefined>,
        required: false,
        default: undefined,
    },
});

const emits = defineEmits(['confirm', 'otherVerify', 'update:visible']);

const scene = ref<'verify' | 'error'>('verify');
const showOtherVerify = ref(true);
const verifyLoading = ref(false);

// 兼容 vue3.2 的写法，不能用 defineModel
const dialogVisible = computed({
    get: () => props.visible,
    set: (val: boolean) => emits('update:visible', val),
});

function hide() {
    dialogVisible.value = false;
}

async function verify() {
    if (verifyLoading.value) {
        return;
    }

    scene.value = 'verify';
    verifyLoading.value = true;

    // 需要放在 nextTick 中，否则 loading 可能不转圈
    nextTick(async () => {
        const optionsRes = await ucApi.passkey.bizStart({ biz: props.biz, ...props.extraParams });
        if (!optionsRes.success) {
            toast.error(optionsRes.msg);
            verifyLoading.value = false;
            return;
        }

        const optionsData = optionsRes.data as PublicKeyCredentialCreationOptionsJSON;
        let verificationResp;

        try {
            // 调用 passkey 验证器，如果失败停留在当前状态
            verificationResp = await startAuthentication({ optionsJSON: optionsData });
        } catch {
            verifyLoading.value = false;
            return;
        }

        try {
            // 服务端验证
            const resp = await ucApi.passkey.bizVerify({
                biz: props.biz,
                bizParams: JSON.stringify(props.bizParams),
                response: verificationResp,
                ...props.extraParams,
            });

            if (!resp.success) {
                toast.error(resp.msg);
                verifyLoading.value = false;
                return;
            }

            hide();
            emits('confirm', resp.data.passkeyToken);
        } catch {
            scene.value = 'error';
            verifyLoading.value = false;
        }
    });
}

function otherVerify() {
    hide();
    emits('otherVerify');
};

// 查询当前业务是否允许降级，允许降级才显示切换其他验证方式的入口
const downgradeInfo: Partial<Record<PasskeyBizType, boolean>> = {};
async function getDowngradeInfo() {
    // 如果传入了 downgradeAble，则直接使用传入的值
    if (props.downgradeAble !== undefined) {
        showOtherVerify.value = props.downgradeAble;
        return;
    }

    // 否则，走接口查询当前业务是否允许降级，并缓存结果
    if (!(props.biz in downgradeInfo)) {
        const isDowngradeEnable = await ucApi.passkeyBiz.isDowngradeEnable(props.biz);
        downgradeInfo[props.biz] = isDowngradeEnable.data;
    }

    showOtherVerify.value = downgradeInfo[props.biz] ?? true;
}

// 监听 dialogVisible 变化，查询当前业务是否允许降级，同时还原状态
watch(() => dialogVisible.value, (val) => {
    if (val) {
        verifyLoading.value = false;
        verify();
        getDowngradeInfo();
    }
}, { immediate: true });

defineExpose({
    enablePasskey: () => {
        return browserSupportsWebAuthn();
    },
});
</script>

<template>
    <BaseDialog
        v-if="scene === 'verify'"
        v-model="dialogVisible"
        width="420"
        close-on-press-escape
    >
        <div class="-mt-18px mb-18px">
            <img
                class="w-64px h-64px"
                alt="icon-passkey"
                src="@img/user/passkey/passkey.svg"
            >
        </div>
        <p class="c-[--gray-900] text-18px lh-28px fw-500 text-lg-m">
            {{ $t('uc.passkey_verify.verify_passkey_info') }}
        </p>
        <p class="c-[--gray-600] text-14px lh-20px text-sm-r mt-8px">
            {{ $t('uc.passkey_verify.verify_passkey_msg') }}
        </p>
        <div class="flex justify-between gap-x-20px mt-34px">
            <BaseButton
                class="flex-1"
                type="black"
                size="small-large"
                @click="verify"
            >
                <BaseIcon v-show="verifyLoading" name="loading" />
                <span v-show="!verifyLoading">{{ $t('uc.passkey_verify.verify_passkey_btn') }}</span>
            </BaseButton>
        </div>
        <div v-if="showOtherVerify" class="mt-24px text-center">
            <span
                class="cursor-pointer c-[--primary] text-14px lh-20px fw-500 text-sm-m"
                @click="otherVerify"
            >
                {{ $t('uc.passkey_verify.passkey_not_working') }}
            </span>
        </div>
    </BaseDialog>
    <BaseDialog
        v-else-if="scene === 'error'"
        v-model="dialogVisible"
        width="420"
        close-on-press-escape
    >
        <div class="-mt-18px mb-18px">
            <img
                class="w-64px h-64px"
                alt="icon-warning"
                src="@img/common/icon-warning.svg"
            >
        </div>
        <p class="c-[--gray-900] text-18px lh-28px fw-500 text-lg-m">
            {{ $t('uc.passkey_verify.passkey_verify_failed') }}
        </p>
        <p class="c-[--gray-600] text-14px lh-20px text-sm-r mt-8px">
            {{ $t('uc.passkey_verify.passkey_verify_failed_desc') }}
        </p>
        <div class="flex justify-between gap-x-20px mt-34px">
            <BaseButton
                class="flex-1"
                type="black"
                size="small-large"
                @click="verify"
            >
                {{ $t('uc.passkey_verify.retry_btn') }}
            </BaseButton>
        </div>
        <div v-if="showOtherVerify" class="mt-24px text-center">
            <span
                class="cursor-pointer c-[--primary] text-14px lh-20px fw-500 text-sm-m"
                @click="otherVerify"
            >
                {{ $t('uc.passkey_verify.other_method_verify') }}
            </span>
        </div>
    </BaseDialog>
</template>
